import * as React from 'react';

import _ from 'underscore';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import hoistNonReactStatics from 'js/lib/hoistNonReactStatics';

// TODO: Migrate off of Backbone
// eslint-disable-next-line no-restricted-imports
import type ItemMetadata from 'pages/open-course/common/models/itemMetadata';
import { HONORS_TRACK } from 'pages/open-course/common/models/tracks';

import type CertificateStoreClass from 'bundles/in-course-stores/stores/CertificateStore';
import {
  hasPassedCourse as hasPassedCourseComputer,
  hasPassedTrack as hasPassedTrackComputer,
  isLagging as isLaggingComputer,
} from 'bundles/in-course-stores/stores/StoreComputationHelpers';
import type VerificationStoreClass from 'bundles/in-course-stores/stores/VerificationStore';
import { getIsAssignmentPartForSplitPeer, getReviewPartFromSplitPeer } from 'bundles/learner-progress/utils/Item';
import { createComputedGradedAssignmentGroupFromStores } from 'bundles/learner-progress/utils/withComputedGradedAssignmentGroup';
import { createComputedItemFromStores } from 'bundles/learner-progress/utils/withComputedItem';

import type { CourseProgress } from '../types/Course';
import type { GradedAssignmentGroup } from '../types/GradedAssignmentGroup';
import type { Item } from '../types/Item';
import type { Stores as AssignmentItemsStores } from '../types/Stores';

type OtherStores = {
  VerificationStore: VerificationStoreClass;
  CertificateStore: CertificateStoreClass;
};

type Stores = AssignmentItemsStores & OtherStores;

type PropsFromStores = {
  computedCourseProgress: CourseProgress;
};

// NOTE: in all newer code, assignments are called "assignments"
// Previously, we used the term "Gradable" as is present in some parts of the codebase.
// For now, use of "Assignments" is scoped to work in `course-assignments-v2`,
// but it should be adjusted in other places over time.
const getAssignmentItems = ({
  CourseStore,
  ProgressStore,
  CourseScheduleStore,
  SessionStore,
  CourseViewGradeStore,
}: AssignmentItemsStores) => {
  const assignmentItemMetadatas = CourseStore.getGradableItems();
  const assignmentItems: Array<Item> = [];
  assignmentItemMetadatas.forEach((itemMetadata: ItemMetadata) => {
    const computedItem = createComputedItemFromStores({
      itemMetadata,
      CourseStore,
      ProgressStore,
      CourseScheduleStore,
      SessionStore,
      CourseViewGradeStore,
    });
    assignmentItems.push(computedItem);
    if (getIsAssignmentPartForSplitPeer(computedItem)) {
      const reviewPart = getReviewPartFromSplitPeer(computedItem);
      if (reviewPart) {
        assignmentItems.push(reviewPart);
      }
    }
  });
  return assignmentItems;
};

const createComputedCourseFromStores = ({
  CourseStore,
  SessionStore,
  CourseScheduleStore,
  CourseViewGradeStore,
  ProgressStore,
  VerificationStore,
  CertificateStore,
}: Stores): CourseProgress => {
  const courseId = CourseStore.getCourseId();
  const courseSlug = CourseStore.getCourseSlug();
  const courseName = CourseStore.getCourseName();
  const currentWeek = SessionStore.getCurrentWeek();
  const sessionEnded = SessionStore.hasEnded();
  const isDegreeSession = SessionStore.isDegreeSession() && SessionStore.isSessionPrivate();
  const isMixedGradePolicy = CourseStore.isMixedGradePolicy(); // Used only in AssignmentStatesHeroUnit.tsx and AssignmentsPage.tsx
  const isMasteryGradePolicy = CourseStore.isMasteryGradePolicy(); // Used only in AssignmentStatesHeroUnit.tsx
  const gradedAssignmentGroupGrades = CourseViewGradeStore.getGradedAssignmentGroupGrades(); // Used only to compute gradedAssignmentGroups below

  // Used only in AssignmentsPage.tsx, passed to GradedAssignmentGroupTable.tsx:
  const gradedAssignmentGroups = Object.keys(gradedAssignmentGroupGrades || {}).reduce(
    (groups, gradedAssignmentGroupId) => {
      const computedGradedAssignmentGroup = createComputedGradedAssignmentGroupFromStores({
        gradedAssignmentGroupGrade: gradedAssignmentGroupGrades?.[gradedAssignmentGroupId],
        CourseStore,
        ProgressStore,
        CourseScheduleStore,
        SessionStore,
        CourseViewGradeStore,
      });
      if (computedGradedAssignmentGroup) {
        groups.push(computedGradedAssignmentGroup);
      }
      return groups;
    },
    [] as GradedAssignmentGroup[]
  );

  // capturing all item ids that are part of a graded assignment group
  const gradedAssignmentGroupItemIds: Array<string> = CourseStore.getGradedAssignmentGroups().reduce(
    (acc, gradedAssignmentGroup) => {
      return acc.concat(gradedAssignmentGroup.itemIds);
    },
    [] as string[]
  );

  const assignmentItems = getAssignmentItems({
    CourseStore,
    ProgressStore,
    CourseScheduleStore,
    SessionStore,
    CourseViewGradeStore,
  });

  // using the term "core" to describe non-honors assignments
  const coreAssignmentItems = assignmentItems.filter((item) => item.isCore);
  const ungroupedAssignmentItems = coreAssignmentItems
    .filter((item) => !gradedAssignmentGroupItemIds.includes(item.id))
    .sort((itemA, itemB) => {
      if (itemA.deadline && itemB.deadline) {
        return +itemA.deadline - +itemB.deadline;
      } else {
        return 0;
      }
    });
  const honorsAssignmentItems = assignmentItems.filter((item) => item.isHonors);

  const failedCoreAssignmentItems = coreAssignmentItems.filter((item) => item.isFailed);

  const computedCourseProgress: CourseProgress = {
    courseId,
    courseSlug,
    courseName,
    course: CourseStore.getMetadata(),
    isGuidedProject: CourseStore.isGuidedProject(),
    courseProgress: ProgressStore.courseProgress,
    courseViewGrade: CourseViewGradeStore.getCourseViewGrade(),

    currentWeek,
    sessionEnded,

    isDegreeSession,
    isMixedGradePolicy, // Used only in AssignmentsPageHeroUnit.tsx and AssignmentsPage.tsx and comes from from CourseStore
    isMasteryGradePolicy, // Used only in AssignmentStatesHeroUnit.tsx and comes from from CourseStore

    isLagging: isLaggingComputer(CourseStore, CourseScheduleStore, ProgressStore), // Used in SessionBox.tsx. Many helper functions are used only in this code path.
    hasPassed: hasPassedCourseComputer(CourseViewGradeStore, VerificationStore), // Used in EnrollBox.tsx and AssignmentsPageHeroUnit.tsx
    hasPassedHonorsTrack: hasPassedTrackComputer(CourseViewGradeStore, VerificationStore, HONORS_TRACK), // Used only in CoursePassedBox

    completionTime: CertificateStore.getCourseCompletionTime({
      courseId,
    }),

    assignmentItems, // used only in AssignmentsPageHeroUnit and AssignmentStatesHeroUnit
    ungroupedAssignmentItems, // used only in UngroupedAssignmentsTable
    honorsAssignmentItems, // used only in HonorsAssignmentGroupTable
    failedCoreAssignmentItems, // used only in AssignmentStatesHeroUnit

    gradedAssignmentGroups,

    // TODO (@sgogia): consolidate these with the above item computations using ComputedItem
    finalPercentCourseGrade: CourseViewGradeStore.getFinalPercentCourseGrade(), // Used only in DegreeSessionHeroUnit.tsx
  };

  return computedCourseProgress;
};

const withComputedCourseProgressImpl = <T extends {}>(
  WrappedComponent: React.ComponentType<T>
): React.ComponentClass<T & PropsFromStores> => {
  const componentName = WrappedComponent.displayName || WrappedComponent.name;

  class HOC extends React.Component<T & PropsFromStores> {
    static displayName = `withComputedCourseProgress(${componentName})`;

    render() {
      return <WrappedComponent {...this.props} />;
    }
  }

  hoistNonReactStatics(HOC, WrappedComponent);

  return _.compose(
    connectToStores<PropsFromStores, {}, Stores>(
      [
        'CourseStore',
        'SessionStore',
        'CourseScheduleStore',
        'CourseViewGradeStore',
        'ProgressStore',
        'VerificationStore',
        'CertificateStore',
      ],
      ({
        CourseStore,
        SessionStore,
        CourseScheduleStore,
        CourseViewGradeStore,
        ProgressStore,
        VerificationStore,
        CertificateStore,
      }) => ({
        computedCourseProgress: createComputedCourseFromStores({
          CourseStore,
          SessionStore,
          CourseScheduleStore,
          CourseViewGradeStore,
          ProgressStore,
          VerificationStore,
          CertificateStore,
        }),
      })
    )
  )(HOC);
};

/**
 *
 * @deprecated Please be careful when using this component. It does heavy computation and should be used sparingly.
 * @param component
 * @returns
 */
export default function withComputedCourseProgress(component?: React.ComponentType) {
  // If this is called with no arguments (usually inside _.compose),
  // we will return a closure to be called later.
  if (!component) {
    return function (Component: React.ComponentType) {
      return withComputedCourseProgressImpl(Component);
    };
  } else {
    return withComputedCourseProgressImpl(component);
  }
}

export { createComputedCourseFromStores };
